import { useState, useEffect } from 'react';
import { Check, Filter, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useNotificationCountsQuery } from '@/lib/query-options';

interface FilterDropdownProps {
  organizationId: number;
  selectedSources: string[];
  selectedWarningLevels: string[];
  startDate?: string;
  endDate?: string;
  activeFilterCount: number;
  onApplyFilters: (filters: {
    sources: string[];
    warningLevels: string[];
    startDate?: string;
    endDate?: string;
  }) => void;
}

const SOURCES = [
  { value: 'ACTIONS', label: 'Actions', color: 'text-blue-800' },
  { value: 'CONFIGURATION', label: 'Configuration', color: 'text-purple-800' },
  { value: 'CAMPAIGN_CREATION', label: 'Campaign Creation', color: 'text-amber-800' },
  { value: 'onboarding', label: 'Onboarding', color: 'text-teal-800' },
  { value: 'keyword_sync', label: 'Policy Violations', color: 'text-red-800' },
  { value: 'googleads_campaign_sync', label: 'Google Ads Sync', color: 'text-orange-800' },
];

const REPORT_SOURCES = [
  { value: 'WEEKLY_PERFORMANCE_REPORT', label: 'Weekly Reports' },
  { value: 'daily_summary', label: 'Daily Summary' },
  { value: 'strategic_report', label: '6 Month Strategic' },
  { value: 'intelligence_report', label: 'Intelligence' },
];

const WARNING_LEVELS = [
  { value: 'ERROR', label: 'Error', color: 'text-red-800' },
  { value: 'WARNING', label: 'Warning', color: 'text-yellow-800' },
  { value: 'SUCCESS', label: 'Success', color: 'text-green-800' },
  { value: 'INFO', label: 'Info', color: 'text-gray-900' },
];

const DATE_PRESETS: { value: string; label: string; start_date?: string; end_date?: string }[] = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'today',
    label: 'Today',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  },
  {
    value: '7days',
    label: '7 Days',
    start_date: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  },
  {
    value: '30days',
    label: '30 Days',
    start_date: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  },
];

export function FilterDropdown({
  organizationId,
  selectedSources,
  selectedWarningLevels,
  startDate,
  endDate,
  activeFilterCount,
  onApplyFilters,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isReportsExpanded, setIsReportsExpanded] = useState(false);

  // Temporary filter state (not applied until "Apply Filters" is clicked)
  const [tempSources, setTempSources] = useState<string[]>(selectedSources);
  const [tempWarningLevels, setTempWarningLevels] = useState<string[]>(selectedWarningLevels);
  const [tempDateRange, setTempDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: startDate ? new Date(startDate) : undefined,
    to: endDate ? new Date(endDate) : undefined,
  });

  // Sync temp state with props when they change externally
  useEffect(() => {
    setTempSources(selectedSources);
    setTempWarningLevels(selectedWarningLevels);
    setTempDateRange({
      from: startDate ? new Date(startDate) : undefined,
      to: endDate ? new Date(endDate) : undefined,
    });
  }, [selectedSources, selectedWarningLevels, startDate, endDate]);

  // Fetch notification counts for filter badges
  const { data: counts } = useNotificationCountsQuery(organizationId, {
    is_archived: false,
  });

  const toggleSource = (source: string) => {
    setTempSources((prev) =>
      prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]
    );
  };

  const reportSourceValues = REPORT_SOURCES.map((r) => r.value);
  const allReportsSelected = reportSourceValues.every((v) => tempSources.includes(v));
  const someReportsSelected = reportSourceValues.some((v) => tempSources.includes(v));

  const toggleAllReports = () => {
    if (allReportsSelected) {
      // Uncheck all reports
      setTempSources((prev) => prev.filter((s) => !reportSourceValues.includes(s)));
    } else {
      // Check all reports
      setTempSources((prev) => [...new Set([...prev, ...reportSourceValues])]);
    }
  };

  const toggleWarningLevel = (level: string) => {
    setTempWarningLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const handleDatePresetChange = (value: string) => {
    const preset = DATE_PRESETS.find((p) => p.value === value);
    if (preset?.start_date && preset?.end_date) {
      setTempDateRange({
        from: new Date(preset.start_date),
        to: new Date(preset.end_date),
      });
    } else {
      // All - clear date range
      setTempDateRange({ from: undefined, to: undefined });
    }
  };

  const getSelectedDatePreset = (): string => {
    if (!tempDateRange.from || !tempDateRange.to) return 'all';
    const fromStr = format(tempDateRange.from, 'yyyy-MM-dd');
    const toStr = format(tempDateRange.to, 'yyyy-MM-dd');
    const preset = DATE_PRESETS.find(
      (p) => p.start_date === fromStr && p.end_date === toStr
    );
    return preset?.value || 'all';
  };

  const handleApplyFilters = () => {
    onApplyFilters({
      sources: tempSources,
      warningLevels: tempWarningLevels,
      startDate: tempDateRange.from ? format(tempDateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: tempDateRange.to ? format(tempDateRange.to, 'yyyy-MM-dd') : undefined,
    });
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempSources([]);
    setTempWarningLevels([]);
    setTempDateRange({ from: undefined, to: undefined });
  };

  // Count how many filters are applied temporarily
  const tempFilterCount =
    (tempSources.length > 0 ? 1 : 0) +
    (tempWarningLevels.length > 0 ? 1 : 0) +
    (tempDateRange.from && tempDateRange.to ? 1 : 0);

  return (
    // #4624: badge is a sibling of the Button so it can't affect the
    // button's layout regardless of parent context.
    <div className="relative inline-flex">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 w-9 p-0 text-gray-900">
            <Filter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
          {/* Notification Type Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Type</h3>
            <div className="space-y-2">
              {/* Non-Report Sources */}
              {SOURCES.map((source) => {
                const count = counts?.by_source?.[source.value] || 0;
                const isChecked = tempSources.includes(source.value);
                return (
                  <div key={source.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`source-${source.value}`}
                      checked={isChecked}
                      onCheckedChange={() => toggleSource(source.value)}
                    />
                    <Label
                      htmlFor={`source-${source.value}`}
                      className={cn(
                        'flex-1 cursor-pointer text-sm font-medium',
                        isChecked ? source.color : 'text-gray-900'
                      )}
                    >
                      {source.label}
                      {count > 0 && (
                        <span className="ml-1 text-gray-500">({count})</span>
                      )}
                    </Label>
                  </div>
                );
              })}

              {/* Reports Section with Submenu */}
              <div className="mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="reports-all"
                    checked={allReportsSelected ? true : someReportsSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAllReports}
                  />
                  <Label
                    htmlFor="reports-all"
                    className={cn(
                      'flex-1 cursor-pointer text-sm font-medium',
                      someReportsSelected ? 'text-indigo-800' : 'text-gray-900'
                    )}
                  >
                    Reports
                  </Label>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 cursor-pointer transition-transform text-gray-500 hover:text-gray-700',
                      isReportsExpanded && 'rotate-90'
                    )}
                    onClick={() => setIsReportsExpanded(!isReportsExpanded)}
                  />
                </div>

                {isReportsExpanded && (
                  <div className="space-y-2 ml-6 mt-2">
                    {REPORT_SOURCES.map((report) => {
                      const count = counts?.by_source?.[report.value] || 0;
                      const isChecked = tempSources.includes(report.value);
                      return (
                        <div key={report.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`report-${report.value}`}
                            checked={isChecked}
                            onCheckedChange={() => toggleSource(report.value)}
                          />
                          <Label
                            htmlFor={`report-${report.value}`}
                            className="flex-1 cursor-pointer text-sm font-medium text-gray-900"
                          >
                            {report.label}
                            <span className="ml-1 text-gray-500">({count})</span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Warning Levels Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Severity</h3>
            <div className="space-y-2">
              {WARNING_LEVELS.map((level) => {
                const count = counts?.by_warning_level?.[level.value] || 0;
                const isChecked = tempWarningLevels.includes(level.value);
                return (
                  <div key={level.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`level-${level.value}`}
                      checked={isChecked}
                      onCheckedChange={() => toggleWarningLevel(level.value)}
                    />
                    <Label
                      htmlFor={`level-${level.value}`}
                      className={cn(
                        'flex-1 cursor-pointer text-sm font-medium',
                        isChecked ? level.color : 'text-gray-900'
                      )}
                    >
                      {level.label}
                      {count > 0 && (
                        <span className="ml-1 text-gray-500">({count})</span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Date Range Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Date Range</h3>
            <Tabs value={getSelectedDatePreset()} onValueChange={handleDatePresetChange}>
              <TabsList className="grid w-full grid-cols-4">
                {DATE_PRESETS.map((preset) => (
                  <TabsTrigger key={preset.value} value={preset.value}>
                    {preset.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t p-3 bg-gray-50 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={tempFilterCount === 0}
            className="text-gray-700"
          >
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleApplyFilters}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
          >
            <Check className="h-4 w-4 mr-1" />
            Apply Filters
            {tempFilterCount > 0 && ` (${tempFilterCount})`}
          </Button>
        </div>
      </PopoverContent>
      </Popover>
      {activeFilterCount > 0 && (
        <span className="pointer-events-none absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#290BD9] text-white text-[10px] flex items-center justify-center">
          {activeFilterCount}
        </span>
      )}
    </div>
  );
}
